'use client';

import { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import type { ClientEnvType } from '~/env-types';
import { DatadogBrowserDestination } from '~/lib/datadog-browser-destination';
import {
  sanitizeDatadogEvent,
  sanitizeSensitiveString,
} from '~/lib/datadog-sanitizer';
import { initBrowserLogger } from '~/lib/logger';
import { getCurrentSearch } from '~/utils/browser-location';

let datadogInitialized = false;

export const resetDatadogInit = () => {
  datadogInitialized = false;
};

export const DatadogInit = ({ env }: { env: ClientEnvType }) => {
  useEffect(() => {
    if (datadogInitialized) return;

    const isHeadless =
      globalThis.navigator.userAgent.toLowerCase().includes('headless') ||
      globalThis.navigator.userAgent.startsWith('Pingdom');
    const isDatadogInitialized =
      typeof datadogRum.getInternalContext() !== 'undefined';

    if (
      !isHeadless &&
      !isDatadogInitialized &&
      env.DATADOG_APPLICATION_ID &&
      env.DATADOG_CLIENT_TOKEN &&
      env.DATADOG_SITE &&
      env.DATADOG_SERVICE &&
      env.DATADOG_ENV &&
      env.DATADOG_RUM_SESSION_SAMPLE_RATE &&
      env.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE
    ) {
      const urlParams = new URLSearchParams(getCurrentSearch());
      const isMonitor = urlParams.get('is_monitor') === '1';
      let sessionSampleRate = Number(env.DATADOG_RUM_SESSION_SAMPLE_RATE) || 0;
      let sessionReplaySampleRate =
        Number(env.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE) || 0;
      if (isMonitor) {
        sessionSampleRate = 10;
        sessionReplaySampleRate = 10;
      }

      datadogRum.init({
        applicationId: env.DATADOG_APPLICATION_ID,
        clientToken: env.DATADOG_CLIENT_TOKEN,
        site: env.DATADOG_SITE,
        service: env.DATADOG_SERVICE,
        env: env.DATADOG_ENV,
        sessionSampleRate,
        sessionReplaySampleRate,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask',
        allowedTracingUrls: ['https://api-production.avodahmed.com/'],
        beforeSend: (event) => {
          if (event.type === 'resource' && event.resource.url) {
            event.resource.url = sanitizeSensitiveString(event.resource.url);
          }
          sanitizeDatadogEvent(event);
          return true;
        },
        version: env.RELEASE_TAG,
      });

      datadogRum.setGlobalContextProperty('user_business', 'med');
      datadogRum.setGlobalContextProperty('user_env', env.DATADOG_ENV);

      datadogInitialized = true;
    }

    // Initialize browser logs (via med-logger → DatadogBrowserDestination).
    // Requires fewer env vars than RUM. Call even if RUM didn't init.
    if (
      !isHeadless &&
      env.DATADOG_CLIENT_TOKEN &&
      env.DATADOG_SITE &&
      env.DATADOG_ENV
    ) {
      DatadogBrowserDestination.init({
        clientToken: env.DATADOG_CLIENT_TOKEN,
        site: env.DATADOG_SITE,
        env: env.DATADOG_ENV,
        version: env.RELEASE_TAG,
        service: env.DATADOG_SERVICE,
      });
      initBrowserLogger(new DatadogBrowserDestination());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once per page load, env values read once at init
  }, []);

  return null;
};
